import { captureException } from '@sentry/react';
import { StateCreator } from 'zustand';

import { authorizedAxiosInstance } from '@/clients';

import { BillingActions, BillingState } from '../types';

const FRESH_MS = 60_000;

export const createBillingActions: StateCreator<
    BillingState & BillingActions,
    [['zustand/immer', never]],
    [],
    BillingActions
> = (set, get, store) => ({
    // Internal
    _setSnapshot: (snap) => {
        set((s) => {
            s.snapshot = snap;
            s.lastFetchedAt = Date.now();
        });
    },
    // Core
    fetchSnapshot: async ({ force } = {}) => {
        const state = get();

        const isFresh = state.lastFetchedAt && Date.now() - state.lastFetchedAt < FRESH_MS;

        if (!force && isFresh) {
            return;
        }

        // 🔐 request dedupe (enterprise critical)
        if (state._inflightFetch) {
            return state._inflightFetch;
        }

        const promise = (async () => {
            set((s) => {
                s.loading = true;
                s.error = undefined;
            });

            try {
                const res = await authorizedAxiosInstance.get('/api/billing/snapshot');

                set((s) => {
                    s.snapshot = res.data;
                    s.loading = false;
                    s.lastFetchedAt = Date.now();
                    s._inflightFetch = null;
                });
            } catch (error) {
                captureException(error);

                set((s) => {
                    s.loading = false;
                    s.error = 'Failed to load billing';
                    s._inflightFetch = null;
                });
            }
        })();

        set((s) => {
            s._inflightFetch = promise;
        });

        return promise;
    },

    refreshSnapshot: async () => {
        return get().fetchSnapshot({ force: true });
    },
    // Aliases
    changePlan: async (input) => {
        const prev = get().snapshot;

        set((s) => {
            s.mutating = true;
        });

        try {
            await authorizedAxiosInstance.post('/api/billing/change-plan', input);

            // ✅ always re-sync after mutation
            await get().refreshSnapshot();
        } catch (error) {
            captureException(error);

            // rollback safety (future-proof)
            set((s) => {
                s.snapshot = prev ?? null;
            });

            throw error;
        } finally {
            set((s) => {
                s.mutating = false;
            });
        }
    },
    updateSeats: async (quantity) => {
        const prev = get().snapshot;

        // 🚀 optimistic update
        set((s) => {
            s.mutating = true;

            if (s.snapshot) {
                s.snapshot.seats.activeSeats = quantity;
            }
        });

        try {
            await authorizedAxiosInstance.post('/api/billing/update-seats', { quantity });

            await get().refreshSnapshot();
        } catch (error) {
            captureException(error);

            // 🔐 rollback
            set((s) => {
                s.snapshot = prev ?? null;
            });

            throw error;
        } finally {
            set((s) => {
                s.mutating = false;
            });
        }
    },
    toggleAddOn: async (code, enabled) => {
        const prev = get().snapshot;

        // optimistic
        set((s) => {
            s.mutating = true;

            if (!s.snapshot?.addOns) {
                return;
            }

            const addon = s.snapshot.addOns.find((a: any) => a.code === code);

            if (addon) {
                addon.enabled = enabled ? true : false;
            }
        });

        try {
            await authorizedAxiosInstance.post('/api/billing/toggle-addon', { code, enabled });

            await get().refreshSnapshot();
        } catch (error) {
            captureException(error);

            // rollback
            set((s) => {
                s.snapshot = prev ?? null;
            });

            throw error;
        } finally {
            set((s) => {
                s.mutating = false;
            });
        }
    },
});
